import React, { useState } from "react";
import { Link } from "react-router";
import { useConsent } from "@/hooks/useConsent";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

export const ConsentBanner: React.FC = () => {
  const { showBanner, consent, acceptAll, rejectAll, updateConsent } = useConsent();
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(consent?.analytics ?? false);
  const [marketingEnabled, setMarketingEnabled] = useState(consent?.marketing ?? false);
  const [functionalEnabled, setFunctionalEnabled] = useState(consent?.functional ?? false);

  if (!showBanner) {
    return null;
  }

  const handleSavePreferences = () => {
    updateConsent({
      analytics: analyticsEnabled,
      marketing: marketingEnabled,
      functional: functionalEnabled,
    });
  };

  return (
    // Deliberately NOT role="dialog": the banner is non-blocking (users may
    // browse before choosing), and claiming dialog/modal semantics without a
    // focus trap tells screen-reader users the page behind is inert when it
    // isn't (#786). A labelled region gives landmark navigation instead.
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl"
    >
      <div className="max-w-7xl mx-auto px-4 py-4">
        {!showPreferences ? (
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900 mb-1">
                We use cookies to improve your experience
              </p>
              <p className="text-xs text-gray-600">
                We use cookies for essential functionality, analytics, and personalisation. See our{" "}
                {/* Padding offset by an equal negative margin: a 28px tap
                    target without changing the line height (#1038). */}
                <Link
                  to="/privacy-settings"
                  className="inline-block py-1.5 -my-1.5 underline text-blue-600 hover:text-blue-800"
                >
                  privacy settings
                </Link>{" "}
                for details.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowPreferences(true)}
                className="text-xs"
              >
                Manage Preferences
              </Button>
              <Button size="sm" variant="outline" onClick={rejectAll} className="text-xs">
                Reject All
              </Button>
              <Button size="sm" onClick={acceptAll} className="text-xs">
                Accept All
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm font-semibold text-gray-900">Cookie Preferences</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
                <div>
                  <Label className="text-sm font-medium">Essential</Label>
                  <p className="text-xs text-gray-500">Required for core functionality</p>
                </div>
                <Switch checked disabled aria-label="Essential cookies (always on)" />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
                <div>
                  <Label className="text-sm font-medium" htmlFor="analytics-toggle">
                    Analytics
                  </Label>
                  <p className="text-xs text-gray-500">Help us improve the portal</p>
                </div>
                <Switch
                  id="analytics-toggle"
                  checked={analyticsEnabled}
                  onCheckedChange={setAnalyticsEnabled}
                  aria-label="Analytics cookies"
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
                <div>
                  <Label className="text-sm font-medium" htmlFor="marketing-toggle">
                    Marketing
                  </Label>
                  <p className="text-xs text-gray-500">Personalised content and ads</p>
                </div>
                <Switch
                  id="marketing-toggle"
                  checked={marketingEnabled}
                  onCheckedChange={setMarketingEnabled}
                  aria-label="Marketing cookies"
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
                <div>
                  <Label className="text-sm font-medium" htmlFor="functional-toggle">
                    Functional
                  </Label>
                  <p className="text-xs text-gray-500">Enhanced features and preferences</p>
                </div>
                <Switch
                  id="functional-toggle"
                  checked={functionalEnabled}
                  onCheckedChange={setFunctionalEnabled}
                  aria-label="Functional cookies"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowPreferences(false)}
                className="text-xs"
              >
                Back
              </Button>
              <Button size="sm" onClick={handleSavePreferences} className="text-xs">
                Save Preferences
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsentBanner;
